import { action, makeObservable, observable, runInAction } from 'mobx';
import { Theme, ThemeConfigurationType } from '@monorepo/client-types';
import { Application } from '@monorepo/types';
import { setCssEnvFromObject, ThemeConfigurations } from './index';

export class ThemeStore {
  @observable
  currentTheme: Theme = Theme.Default;

  @observable
  private project: Application;

  @observable
  themeConfigurations: {
    [key in Theme]?: ThemeConfigurationType;
  } = {};

  constructor({ project }: { project?: Application }) {
    makeObservable(this);
    this.project = project || Application.TTP;
    this.themeConfigurations = ThemeConfigurations[this.project];
  }

  @action
  getThemeConfig(theme: Theme) {
    this.themeConfigurations[theme] = ThemeConfigurations[this.project][theme];
    return this.themeConfigurations[theme];
  }

  initAppCssVars(cssVars: Record<string, string>) {
    setCssEnvFromObject(cssVars);
  }

  getStoredTheme() {
    const storedTheme = localStorage.getItem(this.getThemeKey()) || null;

    return storedTheme as Theme;
  }

  getSystemTheme(): Theme {
    if (typeof window === 'undefined') return Theme.Light;

    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;
    return prefersDark ? Theme.Dark : Theme.Light;
  }

  @action
  switchTheme(theme: Theme) {
    this.initTheme(theme);
  }

  @action
  initTheme(themeToInit?: Theme) {
    const storedTheme = this.getStoredTheme();
    const systemTheme = this.getSystemTheme();
    const theme = themeToInit || storedTheme || systemTheme || Theme.Default;

    const themeConfig = this.getThemeConfig(theme);
    this.initAppCssVars(themeConfig?.cssVars || {});
    this.setTheme(theme);
  }

  getThemeKey() {
    return `${this.project}-theme`;
  }

  setTheme(theme: Theme) {
    runInAction(() => {
      localStorage.setItem(this.getThemeKey(), theme);
      this.currentTheme = theme;
    });
  }

  /**
   * Sets theme for display only without persisting to localStorage.
   * Use for temporary overrides (e.g. login page forcing dark).
   */
  setThemeWithoutPersist(theme: Theme) {
    runInAction(() => {
      const themeConfig = this.getThemeConfig(theme);
      this.initAppCssVars(themeConfig?.cssVars || {});
      this.currentTheme = theme;
    });
  }

  @action
  setProject(project: Application): void {
    this.project = project;
  }

  getAppThemeConfig() {
    return {
      cssVar: true,
      ...this.themeConfigurations[this.currentTheme],
    };
  }
}
